/**
 * Organization / Supervisor Service
 * Talks to the org-scoped supervisor API (seats, roster, aggregated analytics).
 * Analytics-only — never note content. Authorization is enforced server-side
 * (a supervisor can only ever see their own org's clinicians).
 */
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_SUPERVISOR_API_URL ||
  'https://eiglcf9bva.execute-api.us-east-2.amazonaws.com/prod/supervisor';

class OrgService {
  async getAuthHeaders() {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new Error('Authentication required');
    return { 'Content-Type': 'application/json', 'Authorization': token };
  }

  /** Caller's membership + role. Safe to call for any logged-in user. */
  async getMe() {
    try {
      const headers = await this.getAuthHeaders();
      const res = await axios.get(`${API_BASE}/me`, { headers });
      return res.data;
    } catch (error) {
      // Never block the app on this — treat failure as "no org".
      return { success: false, role: 'none' };
    }
  }

  /** Org detail + member roster (admin only). */
  async getOrg() {
    const headers = await this.getAuthHeaders();
    const res = await axios.get(`${API_BASE}/org`, { headers });
    return res.data;
  }

  /** Per-clinician analytics for the five dashboards (admin only). */
  async getAnalytics() {
    const headers = await this.getAuthHeaders();
    const res = await axios.get(`${API_BASE}/analytics`, { headers });
    return res.data;
  }

  /** Former clinicians' analytics retained by the org after seat removal. */
  async getArchived() {
    try {
      const headers = await this.getAuthHeaders();
      const res = await axios.get(`${API_BASE}/archived`, { headers });
      return res.data;
    } catch {
      return { success: false, formerClinicians: [] };
    }
  }

  /**
   * Invite a clinician into a seat (admin only).
   * If no free seat exists, pass addSeat=true to buy one seat first (charged
   * immediately with proration) and then send the invite.
   */
  async invite(email, name, addSeat = false) {
    const headers = await this.getAuthHeaders();
    const res = await axios.post(`${API_BASE}/invite`, { email, name, addSeat }, { headers });
    return res.data;
  }

  /** Remove a clinician and cancel their seat (reduces the subscription). */
  async removeMember(email) {
    const headers = await this.getAuthHeaders();
    const res = await axios.post(`${API_BASE}/remove-member`, { email }, { headers });
    return res.data;
  }

  /** Preview the prorated charge for adding one seat now (admin only). */
  async getSeatPreview() {
    const headers = await this.getAuthHeaders();
    const res = await axios.get(`${API_BASE}/seat-preview`, { headers });
    return res.data;
  }

  /**
   * Start the Supervisor subscription checkout for the given seat quantity.
   * Returns a Stripe-hosted Checkout URL to redirect to.
   */
  async startCheckout(quantity) {
    const headers = await this.getAuthHeaders();
    const res = await axios.post(`${API_BASE}/checkout`, { quantity }, { headers });
    return res.data; // { url }
  }

  /** Open the Stripe billing portal (admin only). */
  async openPortal() {
    const headers = await this.getAuthHeaders();
    const res = await axios.post(`${API_BASE}/portal`, {}, { headers });
    return res.data; // { url }
  }
}

export const orgService = new OrgService();
export default orgService;
